import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ScrollToTopButton — a floating button in the bottom-right corner.
 * Appears only after the user scrolls down a certain threshold.
 * Smoothly scrolls back to the top when clicked.
 */
const ScrollToTopButton = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const hideTimeoutRef = useRef(null);

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY || document.documentElement.scrollTop;
    if (scrolled > 400) {
      // Cancel any pending hide animation
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setLeaving(false);
      setVisible(true);
    } else if (scrolled <= 300) {
      // Only start hiding when we've clearly gone back up
      if (visible) {
        setLeaving(true);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => {
          setVisible(false);
          setLeaving(false);
        }, 250); // matches scroll-top-out animation duration
      }
    }
  }, [visible]);

  useEffect(() => {
    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label={t('scrollTop.label')}
      title={t('scrollTop.label')}
      className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-primary-500 hover:bg-primary-600 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 group active:scale-95 ${
        leaving ? 'animate-scroll-top-out' : 'animate-scroll-top-in'
      }`}
    >
      {/* Arrow up icon */}
      <svg
        className="w-6 h-6 transition-transform duration-300 group-hover:-translate-y-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    </button>
  );
};

export default ScrollToTopButton;